import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth, apiError } from "@/context/AuthContext";

const SWAN_PHOTO = "/api/htt/assets/swan-platinum-photo.jpg";

export default function Login() {
  const { login } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [err, setErr] = useState("");
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setErr("");
    setBusy(true);
    try {
      const user = await login(email, password);
      navigate(user.has_access ? "/dashboard" : "/join");
    } catch (e2) {
      setErr(apiError(e2.response?.data?.detail) || e2.message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="min-h-screen grid lg:grid-cols-2">
      {/* editorial panel */}
      <div className="relative hidden lg:block velvet">
        <img src={SWAN_PHOTO} alt="" className="absolute inset-0 w-full h-full object-cover" />
        <div className="absolute inset-0 bg-gradient-to-t from-purple-deep via-purple-deep/70 to-purple/30" />
        <div className="relative z-10 h-full flex flex-col justify-between p-12">
          <Link to="/" className="flex items-center gap-3" data-testid="login-brand">
            <img src="/api/htt/assets/mark.svg" alt="" className="w-9 h-9 rounded-lg ring-1 ring-gold/40" />
            <span className="text-[11px] uppercase tracking-[0.22em] font-bold text-gold-pale">Oxytocin-Learning</span>
          </Link>
          <div>
            <p className="font-heading italic text-white text-3xl leading-snug max-w-md text-balance">
              “Thank you for telling me. I'm going to stay with this — and I'll tell you what happens next.”
            </p>
            <p className="mt-5 text-[11px] uppercase tracking-[0.22em] text-gold-pale">Forward Ever, Backward Never</p>
          </div>
        </div>
      </div>

      {/* form */}
      <div className="flex items-center justify-center bg-cream px-6 py-16">
        <div className="w-full max-w-sm">
          <p className="text-[11px] uppercase tracking-[0.24em] text-gold-dark font-bold mb-3">Welcome back</p>
          <h1 className="font-heading text-purple text-4xl mb-2">Continue the course</h1>
          <p className="text-lav text-[15px] mb-8">Sign in to pick up exactly where you left off.</p>

          <form onSubmit={submit} className="space-y-4" data-testid="login-form">
            <div>
              <label className="block text-[12px] uppercase tracking-[0.14em] text-lav mb-1.5">Email</label>
              <input
                type="email"
                required
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="w-full rounded-xl border border-line bg-white px-4 py-3 text-ink focus:outline-none focus:ring-2 focus:ring-gold"
                data-testid="login-email"
              />
            </div>
            <div>
              <label className="block text-[12px] uppercase tracking-[0.14em] text-lav mb-1.5">Password</label>
              <input
                type="password"
                required
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="w-full rounded-xl border border-line bg-white px-4 py-3 text-ink focus:outline-none focus:ring-2 focus:ring-gold"
                data-testid="login-password"
              />
            </div>
            {err && (
              <p className="text-[13px] text-red-700 bg-red-50 rounded-lg px-3 py-2" data-testid="login-error">
                {err}
              </p>
            )}
            <button
              type="submit"
              disabled={busy}
              className="w-full bg-purple-deep text-cream font-bold rounded-full py-3.5 hover:bg-purple transition-colors disabled:opacity-60"
              data-testid="login-submit"
            >
              {busy ? "Signing in…" : "Sign in"}
            </button>
          </form>

          <p className="mt-7 text-[14px] text-lav">
            New here?{" "}
            <Link to="/join" className="text-purple-mid font-semibold hover:text-purple underline underline-offset-4" data-testid="login-to-join">
              Join the founding cohort →
            </Link>
          </p>
        </div>
      </div>
    </div>
  );
}
